import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  FunnelAddress,
  FunnelAddressListResponse,
  CrmFunnelsChannelsService,
  OTFunnelAddressListQueryParams,
} from 'src/api/generated';

interface UseFunnelAddressesParams extends OTFunnelAddressListQueryParams {}

interface UseFunnelAddressesResponse extends ApiResponse {
  addresses: FunnelAddress[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Hook to fetch funnel addresses for a specific funnel
 * @param params - Query parameters including funnelId filter
 * @param enabled - Whether the query should be enabled
 * @returns The funnel addresses data, loading state, error state, and pagination functions
 */
export const useFunnelAddresses = (
  params: UseFunnelAddressesParams,
  enabled: boolean = true
): UseFunnelAddressesResponse => {
  const fetchAddresses = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<FunnelAddressListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize = 50 } = params;

    return await CrmFunnelsChannelsService.getFunnelChannels(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const getNextPageParam = (lastPage: FunnelAddressListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/funnel-addresses', params],
    queryFn: fetchAddresses,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const addresses = useMemoizedArrayData<FunnelAddress>(
    data?.pages.flatMap((page) => page.items) || []
  );

  return {
    loading,
    error,
    addresses,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};
